import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Droplet } from "lucide-react";
import { useContent } from "@/lib/content";
import { Overline } from "@/components/Overline";
import { Seo } from "@/components/Seo";

const ease = [0.16, 1, 0.3, 1];

export default function Olivenoel() {
  const c = useContent();
  const t = c.text.olive;
  const img = c.images;

  return (
    <div data-testid="olive-page" className="bg-cream">
      <Seo page="olive" />
      <section className="relative pt-44 pb-24 grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={img.oliveHarvest} alt="Olivenernte" className="w-full h-full object-cover animate-kenburns-slow" />
          <div className="absolute inset-0 bg-ink/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Overline light className="!flex justify-center">Aus eigener Produktion</Overline>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl text-cream tracking-tight">Unser Olivenöl</h1>
          <p className="text-cream/75 font-light mt-5 max-w-xl mx-auto">{t.kicker}</p>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }}>
            <Overline>Kampanien</Overline>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-ink leading-tight mb-6">{t.title}</h2>
            <p className="text-ink-soft font-light leading-relaxed mb-5">{t.p1}</p>
            <p className="text-ink-soft font-light leading-relaxed mb-5">{t.p2}</p>
            <p className="text-ink-soft font-light leading-relaxed">{t.p3}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease }} className="relative">
            <div className="overflow-hidden"><img src={img.oliveBottle} alt="Olivenöl Flasche" className="w-full h-[560px] object-cover animate-kenburns-slow" /></div>
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-wine text-cream px-10 py-5 text-center shadow-2xl">
              <p className="text-xs tracking-[0.25em] uppercase text-gold mb-1">1-Liter-Flasche</p>
              <p className="font-serif text-3xl">{t.price}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-sand grain relative">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Droplet className="mx-auto text-wine mb-5 animate-float" size={34} strokeWidth={1.3} />
          <p className="font-serif text-3xl text-ink mb-4 italic">{t.quote}</p>
          <p className="text-ink-soft font-light mb-3 flex items-center justify-center gap-2"><MapPin size={16} className="text-gold" /> {t.location}</p>
          <p className="text-ink-soft font-light mb-8">Probieren und kaufen Sie unser Olivenöl direkt bei einem Besuch in unserem Lokal.</p>
          <Link to="/kontakt#reserve" data-testid="olive-reserve-btn" className="btn-pop bg-wine text-cream hover:bg-wine-dark px-10 py-4 uppercase text-xs tracking-[0.25em] inline-block">Tisch reservieren</Link>
        </div>
      </section>
    </div>
  );
}
